import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Search, ArrowLeft, Minus, Plus, Trash2, CreditCard, Banknote, X, UserCircle, Percent, StickyNote, Clock, ScanBarcode, UtensilsCrossed, Check, LogIn, LogOut, DollarSign, ChevronRight, RotateCcw, Package, Sparkles, Tag, Receipt, ShieldCheck, WifiOff, Download } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useOnlineStatus, usePWA, precacheApiData } from "@/lib/pwa";

export default function POSPage() {
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const { isInstalled, canInstall, promptInstall } = usePWA();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [settings, setSettings] = useState({});
  const [cart, setCart] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [search, setSearch] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [processing, setProcessing] = useState(false);

  // Customer
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDiscount, setCustomerDiscount] = useState(0);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [customerQuery, setCustomerQuery] = useState("");
  const [customerResults, setCustomerResults] = useState([]);

  // Loyalty scan
  const [showLoyaltyScan, setShowLoyaltyScan] = useState(false);
  const [loyaltyInput, setLoyaltyInput] = useState("");
  const loyaltyInputRef = useRef(null);
  const scanBufferRef = useRef("");
  const scanTimerRef = useRef(null);

  // Dining type
  const [diningTypes, setDiningTypes] = useState([]);
  const [selectedDiningType, setSelectedDiningType] = useState(null);
  const [lastTicket, setLastTicket] = useState(null);

  // Time Clock
  const [showClockIn, setShowClockIn] = useState(false);
  const [showClockOut, setShowClockOut] = useState(false);
  const [clockPin, setClockPin] = useState("");
  const [clockEmployee, setClockEmployee] = useState(null);
  const [clockRoleSelect, setClockRoleSelect] = useState("");

  // Stock check
  const [outOfStock, setOutOfStock] = useState({}); // { product_id: { product_id, name, allow_negative } }

  // Specials / Discounts
  const [appliedSpecials, setAppliedSpecials] = useState([]); // [{ special, amount, variable_amount }]
  const [showSpecialsPanel, setShowSpecialsPanel] = useState(false);
  const [activeSpecials, setActiveSpecials] = useState([]);
  const [showCodeEntry, setShowCodeEntry] = useState(false);
  const [discountCode, setDiscountCode] = useState("");

  // Advanced order time
  const [scheduledTime, setScheduledTime] = useState("");

  // Service Fees
  const [appliedFees, setAppliedFees] = useState([]); // [{ fee, waived }]
  const [allServiceFees, setAllServiceFees] = useState([]);
  const [showWaivePin, setShowWaivePin] = useState(false);
  const [waivePinInput, setWaivePinInput] = useState("");
  const [waiveFeeTarget, setWaiveFeeTarget] = useState(null);

  // Hours enforcement
  const getStoreStatus = () => {
    const hours = settings?.hours_of_operation;
    if (!hours || hours.length === 0) return { open: true };
    const now = new Date();
    const dow = (now.getDay() + 6) % 7; // JS Sunday=0, we want Mon=0
    const dayData = hours.find((h) => h.day === dow);
    if (!dayData) return { open: true };
    if (dayData.closed) return { open: false, reason: "Store is closed today" };
    if (!dayData.open || !dayData.close) return { open: true };
    const [oh, om] = dayData.open.split(":").map(Number);
    const [ch, cm] = dayData.close.split(":").map(Number);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const openMins = oh * 60 + om;
    const closeMins = ch * 60 + cm;
    if (nowMins < openMins) return { open: false, reason: `Store opens at ${dayData.open}` };
    if (nowMins >= closeMins) return { open: false, reason: `Store closed at ${dayData.close}` };
    return { open: true, closesAt: dayData.close, minsLeft: closeMins - nowMins };
  };

  const getDiningTypeStatus = (dt) => {
    if (!dt) return { available: true };
    const storeStatus = getStoreStatus();
    if (!storeStatus.open) return { available: false, reason: storeStatus.reason };
    const hours = settings?.hours_of_operation;
    if (!hours || hours.length === 0) return { available: true };
    const now = new Date();
    const dow = (now.getDay() + 6) % 7;
    const dayData = hours.find((h) => h.day === dow);
    if (!dayData || dayData.closed || !dayData.open || !dayData.close) return { available: true };
    const [oh, om] = dayData.open.split(":").map(Number);
    const [ch, cm] = dayData.close.split(":").map(Number);
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const openMins = oh * 60 + om;
    const closeMins = ch * 60 + cm;
    const beforeOpen = dt.minutes_before_open || 0;
    const beforeClose = dt.minutes_before_close || 0;
    if (beforeOpen > 0 && nowMins < openMins - beforeOpen) {
      return { available: false, reason: `${dt.name} opens ${beforeOpen} min before store` };
    }
    if (beforeClose > 0 && nowMins >= closeMins - beforeClose) {
      return { available: false, reason: `${dt.name} closed ${beforeClose} min before closing` };
    }
    return { available: true };
  };

  const storeStatus = getStoreStatus();
  const [clockStep, setClockStep] = useState("pin"); // "pin" | "role" | "done"
  const [clockTipAmount, setClockTipAmount] = useState("");
  const [clockLoading, setClockLoading] = useState(false);
  const [activeShifts, setActiveShifts] = useState([]);

  // Item configuration (sizes + modifiers)
  const [showItemConfig, setShowItemConfig] = useState(false);
  const [configProduct, setConfigProduct] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configStep, setConfigStep] = useState("size"); // "size" | "modifiers" | "done"
  const [selectedSize, setSelectedSize] = useState(null);
  const [currentModGroupIdx, setCurrentModGroupIdx] = useState(0);
  const [selectedModifiers, setSelectedModifiers] = useState({}); // { modGroupId: [{ modifier, half }] }
  const [includedExtras, setIncludedExtras] = useState({}); // { modifierId: { productId: extraQty } }

  // Combo meal state
  const [showComboConfig, setShowComboConfig] = useState(false);
  const [comboProduct, setComboProduct] = useState(null);
  const [comboLoading, setComboLoading] = useState(false);
  const [comboStep, setComboStep] = useState("size"); // "size" | "slots"
  const [comboSelectedSize, setComboSelectedSize] = useState(null);
  const [comboCurrentSlotIdx, setComboCurrentSlotIdx] = useState(0);
  const [comboSlotSelections, setComboSlotSelections] = useState({}); // { slotIdx: [{ product, configuredModifiers, selectedSize, modTotal }] }
  // Sub-config within a combo slot item (modifier configuration for a picked item)
  const [comboItemConfig, setComboItemConfig] = useState(null); // { slotIdx, itemIdx, product, configData }
  const [comboItemConfigStep, setComboItemConfigStep] = useState("size"); // "size" | "modifiers"
  const [comboItemSelectedSize, setComboItemSelectedSize] = useState(null);
  const [comboItemModifiers, setComboItemModifiers] = useState({});
  const [comboItemModGroupIdx, setComboItemModGroupIdx] = useState(0);

  const fetchData = useCallback(async () => {
    try {
      const [pRes, cRes, sRes, dtRes, shRes, stockRes, specRes, sfRes] = await Promise.all([
        api.get("/products?active_only=true"),
        api.get("/categories"),
        api.get("/settings"),
        api.get("/dining-types"),
        api.get("/timeclock/active"),
        api.get("/inventory/bulk-stock-check"),
        api.get("/specials/active-now"),
        api.get("/service-fees"),
      ]);
      setProducts(pRes.data);
      setCategories(cRes.data);
      setSettings(sRes.data);
      setActiveShifts(shRes.data);
      setOutOfStock(stockRes.data || {});
      setActiveSpecials(specRes.data || []);
      setAllServiceFees((sfRes.data || []).filter((f) => f.is_active));
      const activeDt = dtRes.data.filter((d) => d.is_active);
      setDiningTypes(activeDt);
      // Auto-select first default dining type
      if (!selectedDiningType && activeDt.length > 0) {
        const def = activeDt.find((d) => d.is_default) || activeDt[0];
        setSelectedDiningType(def);
      }
    } catch (err) {
      console.error("Failed to load POS data:", err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Pre-cache API data for offline access after initial load
  useEffect(() => {
    if (products.length > 0) {
      const token = localStorage.getItem("session_token");
      precacheApiData(token);
    }
  }, [products]);

  // Auto-apply service fees when dining type changes
  useEffect(() => {
    if (!selectedDiningType || allServiceFees.length === 0) return;
    const dtId = selectedDiningType.dining_type_id;
    const autoFees = allServiceFees.filter((f) => {
      if (f.auto_apply_type === "all_orders") return true;
      if (f.auto_apply_type === "by_dining_type") {
        return (f.auto_apply_dining_type_ids || []).includes(dtId);
      }
      return false;
    });
    setAppliedFees(autoFees.map((f) => ({ fee: f, waived: false })));
  }, [selectedDiningType, allServiceFees]);

  // Global barcode scanner listener - detects rapid keystrokes ending with Enter
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input field (except loyalty scan input)
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;

      if (e.key === "Enter" && scanBufferRef.current.length >= 4) {
        // Treat buffer as scanned barcode
        const scanned = scanBufferRef.current;
        scanBufferRef.current = "";
        handleLoyaltyScan(scanned);
        return;
      }

      if (e.key.length === 1) {
        scanBufferRef.current += e.key;
        clearTimeout(scanTimerRef.current);
        scanTimerRef.current = setTimeout(() => {
          scanBufferRef.current = "";
        }, 100); // Reset if keystrokes are too slow (not a scanner)
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLoyaltyScan = async (cardNumber) => {
    try {
      const res = await api.get(`/customers?search=${encodeURIComponent(cardNumber)}&include_recent_orders=true`);
      if (res.data.length > 0) {
        selectCustomer(res.data[0]);
        toast.success(`Customer found: ${res.data[0].first_name} ${res.data[0].last_name}`);
      } else {
        toast.error(`No customer found for card: ${cardNumber}`);
      }
    } catch {
      toast.error("Failed to look up loyalty card");
    }
  };

  const handleLoyaltyManualSearch = async () => {
    if (loyaltyInput.trim().length < 2) return;
    await handleLoyaltyScan(loyaltyInput.trim());
    setLoyaltyInput("");
    setShowLoyaltyScan(false);
  };

  const filteredProducts = products.filter((p) => {
    const matchCategory = selectedCategory === "all" || p.category_id === selectedCategory;
    const matchSearch = !search || p.name.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  const addToCart = (product) => {
    // Check store hours
    if (!storeStatus.open) {
      toast.error(storeStatus.reason || "Store is currently closed");
      return;
    }
    // Check dining type availability
    const dtStatus = getDiningTypeStatus(selectedDiningType);
    if (!dtStatus.available) {
      toast.error(dtStatus.reason);
      return;
    }
    // Check stock availability
    const stockInfo = outOfStock[product.product_id];
    if (stockInfo && !stockInfo.allow_negative) {
      toast.error(`${product.name} is out of stock (missing ingredients)`);
      return;
    }
    if (stockInfo && stockInfo.allow_negative) {
      toast.warning(`${product.name} has low/no ingredient stock — selling anyway`);
    }
    // Check if product is a combo
    if (product.is_combo && product.combo_slots?.length > 0) {
      openComboConfig(product);
      return;
    }
    // Check if product needs configuration (sizes or modifiers)
    const hasSizes = !!product.size_group_id;
    const hasMods = (product.modifier_group_ids && product.modifier_group_ids.length > 0) || (product.modifier_groups && product.modifier_groups.length > 0);
    if (hasSizes || hasMods) {
      openItemConfig(product);
    } else {
      addSimpleItem(product);
    }
  };

  // ===== SPECIALS / DISCOUNT FUNCTIONS =====
  const applySpecial = (special, variableAmount = null) => {
    // Check if already applied
    if (appliedSpecials.some((a) => a.special.special_id === special.special_id)) {
      toast.error("This special is already applied");
      return;
    }
    const amt = variableAmount != null ? variableAmount : special.discount_amount;
    setAppliedSpecials((prev) => [...prev, { special, amount: amt }]);
    toast.success(`Applied: ${special.display_name || special.name}`);
    setShowSpecialsPanel(false);
  };

  const removeSpecial = (specialId) => {
    setAppliedSpecials((prev) => prev.filter((a) => a.special.special_id !== specialId));
  };

  const redeemCode = async () => {
    if (!discountCode.trim()) { toast.error("Enter a code"); return; }
    try {
      const res = await api.post("/specials/validate-code", { code: discountCode.trim() });
      const special = res.data;
      applySpecial(special);
      setShowCodeEntry(false);
      setDiscountCode("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid code");
    }
  };

  const calcSpecialsDiscount = () => {
    let totalDiscount = 0;
    for (const applied of appliedSpecials) {
      const { special, amount } = applied;
      if (special.apply_to === "order") {
        if (special.discount_type === "percentage") {
          totalDiscount += subtotal * (amount / 100);
        } else {
          totalDiscount += amount;
        }
      } else if (special.apply_to === "product") {
        // Apply to matching cart items
        for (const item of cart) {
          const matchesProd = special.target_product_ids?.includes(item.product_id);
          const matchesCat = special.target_category_ids?.length > 0 && products.find((p) => p.product_id === item.product_id && special.target_category_ids.includes(p.category_id));
          if (matchesProd || matchesCat) {
            const qty = special.how_often === "all_applicable" ? item.quantity : 1;
            if (special.discount_type === "percentage") {
              totalDiscount += (item.price * qty) * (amount / 100);
            } else {
              totalDiscount += amount * qty;
            }
          }
        }
      }
    }
    return Math.min(Math.round(totalDiscount * 100) / 100, subtotal);
  };

  const addSimpleItem = (product) => {
    setCart((prev) => {
      const existing = prev.find((i) => i.product_id === product.product_id && !i.config_id);
      if (existing) {
        return prev.map((i) => i.product_id === product.product_id && !i.config_id ? { ...i, quantity: i.quantity + 1 } : i);
      }
      return [...prev, { product_id: product.product_id, name: product.name, price: product.price, quantity: 1 }];
    });
  };

  // Build initial modifier selections from product defaults
  const buildDefaultSelections = (resolvedGroups) => {
    const defaults = {};
    for (const g of (resolvedGroups || [])) {
      const gid = g.modifier_group_id;
      const defaultMods = (g.modifiers || []).filter(m => m.is_product_default);
      if (defaultMods.length > 0) {
        defaults[gid] = defaultMods.map(m => ({
          ...m,
          modifier_id: m.modifier_id,
          name: m.name,
          price: m.price,
          placement: "whole",
          qty: m.default_quantity || 1,
        }));
      }
    }
    return defaults;
  };

  const openItemConfig = async (product) => {
    setConfigLoading(true);
    setShowItemConfig(true);
    try {
      const res = await api.get(`/products/${product.product_id}/config`);
      const cfg = res.data;
      setConfigProduct(cfg);
      setSelectedSize(null);
      setSelectedModifiers(buildDefaultSelections(cfg.resolved_modifier_groups));
      setIncludedExtras({});
      setCurrentModGroupIdx(0);
      if (cfg.resolved_size_group && cfg.resolved_size_group.sizes?.length > 0) {
        setConfigStep("size");
      } else if (cfg.resolved_modifier_groups?.length > 0) {
        setConfigStep("modifiers");
      } else {
        // No config needed after all
        addSimpleItem(product);
        setShowItemConfig(false);
      }
    } catch (err) {
      toast.error("Failed to load product options");
      setShowItemConfig(false);
    } finally {
      setConfigLoading(false);
    }
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setSelectedModifiers(buildDefaultSelections(configProduct?.resolved_modifier_groups));
    setIncludedExtras({});
    setCurrentModGroupIdx(0);
    if (configProduct?.resolved_modifier_groups?.length > 0) {
      setConfigStep("modifiers");
    }
  };

  const handleSizeChange = () => {
    setSelectedSize(null);
    setSelectedModifiers({});
    setIncludedExtras({});
    setCurrentModGroupIdx(0);
    setConfigStep("size");
  };

  const getIncludedQty = (mod, sizeId) => {
    if (!mod.included_items?.length) return [];
    return mod.included_items.map((item) => {
      let qty = item.default_qty || 1;
      if (sizeId && item.size_quantities?.[sizeId] !== undefined) {
        qty = item.size_quantities[sizeId];
      }
      return { ...item, included_qty: qty };
    }).filter((item) => item.included_qty > 0);
  };

  const getIncludedExtraPrice = (item) => {
    return item.price_each != null ? item.price_each : 0;
  };

  const calcIncludedExtrasTotal = () => {
    let total = 0;
    for (const [modId, extras] of Object.entries(includedExtras)) {
      // find the modifier to get its included_items
      let mod = null;
      for (const g of (configProduct?.resolved_modifier_groups || [])) {
        mod = (g.modifiers || []).find((m) => m.modifier_id === modId);
        if (mod) break;
      }
      if (!mod) continue;
      for (const [prodId, extraQty] of Object.entries(extras)) {
        if (extraQty > 0) {
          const item = (mod.included_items || []).find((i) => i.product_id === prodId);
          if (item) total += getIncludedExtraPrice(item) * extraQty;
        }
      }
    }
    return Math.round(total * 100) / 100;
  };

  const getModPrice = (mod) => {
    if (selectedSize) {
      const offsets = mod.size_offsets;
      if (offsets) {
        // Handle both array format [{size_id, price}] and dict format {size_id: price}
        if (Array.isArray(offsets)) {
          const match = offsets.find(so => so.size_id === selectedSize.size_id);
          if (match) return parseFloat(match.price) || 0;
        } else if (typeof offsets === "object") {
          const szPrice = offsets[selectedSize.size_id];
          if (szPrice !== undefined && szPrice !== null) return parseFloat(szPrice) || 0;
        }
      }
    }
    return mod.price || 0;
  };

  const getHalfPrice = (mod) => {
    return mod.split_half_price || Math.round(getModPrice(mod) / 2 * 100) / 100;
  };

  const calcModLinePrice = (mod, selData) => {
    const fullPrice = getModPrice(mod);
    const halfPrice = getHalfPrice(mod);
    const placement = selData.placement || "whole";
    const qty = selData.qty || 1;
    const unitPrice = placement === "whole" ? fullPrice : halfPrice;
    return Math.round(unitPrice * qty * 100) / 100;
  };

  const toggleModifier = (modGroup, modifier) => {
    const gid = modGroup.modifier_group_id;
    const max = modGroup.max_select || modGroup.max_selections || 99;
    setSelectedModifiers((prev) => {
      const current = prev[gid] || [];
      const existIdx = current.findIndex((m) => m.modifier_id === modifier.modifier_id);
      if (existIdx >= 0) {
        // Deselect
        return { ...prev, [gid]: current.filter((_, i) => i !== existIdx) };
      }
      if (current.length >= max) return prev;
      return { ...prev, [gid]: [...current, { ...modifier, placement: "whole", qty: 1 }] };
    });
  };

  const updateModPlacement = (modGroup, modifierId, placement) => {
    const gid = modGroup.modifier_group_id;
    setSelectedModifiers((prev) => {
      const current = prev[gid] || [];
      return { ...prev, [gid]: current.map((m) => m.modifier_id === modifierId ? { ...m, placement } : m) };
    });
  };

  const updateModQty = (modGroup, modifierId, delta) => {
    const gid = modGroup.modifier_group_id;
    setSelectedModifiers((prev) => {
      const current = prev[gid] || [];
      // Find the modifier to check max_quantity
      const mod = (modGroup.modifiers || []).find(m => m.modifier_id === modifierId);
      const maxQty = mod?.max_quantity || 99;
      return { ...prev, [gid]: current.map((m) => {
        if (m.modifier_id === modifierId) {
          const newQty = Math.max(1, Math.min(maxQty, (m.qty || 1) + delta));
          return { ...m, qty: newQty };
        }
        return m;
      }) };
    });
  };

  const nextModGroup = () => {
    const groups = configProduct?.resolved_modifier_groups || [];
    const currentGroup = groups[currentModGroupIdx];
    const min = currentGroup?.min_select || currentGroup?.min_selections || 0;
    const selected = (selectedModifiers[currentGroup?.modifier_group_id] || []).length;
    if (selected < min) {
      toast.error(`Please select at least ${min} option(s) for ${currentGroup.name}`);
      return;
    }
    if (currentModGroupIdx < groups.length - 1) {
      setCurrentModGroupIdx(currentModGroupIdx + 1);
    } else {
      addConfiguredItem();
    }
  };

  const prevModGroup = () => {
    if (currentModGroupIdx > 0) {
      setCurrentModGroupIdx(currentModGroupIdx - 1);
    } else if (configProduct?.resolved_size_group) {
      setConfigStep("size");
    }
  };

  const getConfiguredPrice = () => {
    if (!configProduct) return 0;
    let basePrice = configProduct.price || 0;
    if (selectedSize) {
      const map = configProduct.size_prices_map || {};
      const sizePrice = map[selectedSize.size_id];
      if (sizePrice !== undefined && sizePrice !== null) {
        basePrice = parseFloat(sizePrice);
      }
    }
    let modTotal = 0;
    const groups = configProduct.resolved_modifier_groups || [];
    for (const g of groups) {
      const gid = g.modifier_group_id;
      const selected = selectedModifiers[gid] || [];
      const freeAmt = g.free_amount || 0;
      let freeUsed = 0;
      selected.forEach((mod) => {
        const qty = mod.qty || 1;
        const modFreeQty = mod.free_quantity != null ? mod.free_quantity : 0;
        for (let q = 0; q < qty; q++) {
          // Check per-modifier free_quantity first
          if (modFreeQty > 0 && q < modFreeQty) {
            continue; // This unit is free per modifier override
          }
          // Then check group-level free_amount
          if (freeUsed < freeAmt) {
            freeUsed++;
            continue; // This unit is free per group
          }
          modTotal += calcModLinePrice(mod, { ...mod, qty: 1 });
        }
      });
    }
    return Math.round((basePrice + modTotal + calcIncludedExtrasTotal()) * 100) / 100;
  };

  const addConfiguredItem = () => {
    const price = getConfiguredPrice();
    const configId = `cfg_${Date.now()}`;
    const parts = [];
    if (selectedSize) parts.push(selectedSize.name);
    const allMods = [];
    const allIncluded = [];
    for (const g of (configProduct.resolved_modifier_groups || [])) {
      const gid = g.modifier_group_id;
      const selected = selectedModifiers[gid] || [];
      for (const m of selected) {
        const placement = m.placement || "whole";
        const qty = m.qty || 1;
        const linePrice = calcModLinePrice(m, m);
        let label = m.name;
        if (placement === "side_a") label = `${m.name} (Side A)`;
        else if (placement === "side_b") label = `${m.name} (Side B)`;
        if (qty > 1) label = `${qty}x ${label}`;
        parts.push(label);
        allMods.push({ name: m.name, price: linePrice, placement, qty, modifier_id: m.modifier_id });
        // Collect included items for this modifier
        const inclItems = getIncludedQty(m, selectedSize?.size_id);
        for (const inc of inclItems) {
          const extraQty = includedExtras[m.modifier_id]?.[inc.product_id] || 0;
          const totalQty = inc.included_qty + extraQty;
          if (totalQty > 0) {
            const extraCost = extraQty > 0 ? getIncludedExtraPrice(inc) * extraQty : 0;
            parts.push(`${totalQty}x ${inc.name}${extraQty > 0 ? ` (+${extraQty} extra)` : ""}`);
            allIncluded.push({ product_id: inc.product_id, name: inc.name, included_qty: inc.included_qty, extra_qty: extraQty, extra_cost: extraCost });
          }
        }
      }
    }
    setCart((prev) => [...prev, {
      product_id: configProduct.product_id,
      name: configProduct.name,
      price,
      quantity: 1,
      config_id: configId,
      size_name: selectedSize?.name || "",
      size_id: selectedSize?.size_id || "",
      modifiers: allMods,
      included_items: allIncluded,
      config_desc: parts.join(", "),
    }]);
    setShowItemConfig(false);
    setConfigProduct(null);
  };

  // ===== COMBO MEAL FUNCTIONS =====
  const openComboConfig = async (product) => {
    setComboLoading(true);
    setShowComboConfig(true);
    try {
      const res = await api.get(`/products/${product.product_id}/combo-config`);
      const cfg = res.data;
      setComboProduct(cfg);
      setComboSelectedSize(null);
      setComboCurrentSlotIdx(0);
      setComboSlotSelections({});
      setComboItemConfig(null);
      if (cfg.resolved_size_group && cfg.resolved_size_group.sizes?.length > 0) {
        setComboStep("size");
      } else {
        setComboStep("slots");
      }
    } catch (err) {
      toast.error("Failed to load combo options");
      setShowComboConfig(false);
    } finally {
      setComboLoading(false);
    }
  };

  const handleComboSizeSelect = (size) => {
    setComboSelectedSize(size);
  };

  const handleComboSizeNext = () => {
    if (!comboSelectedSize) { toast.error("Please select a size"); return; }
    setComboStep("slots");
    setComboCurrentSlotIdx(0);
  };

  const getComboSlot = () => comboProduct?.resolved_combo_slots?.[comboCurrentSlotIdx];

  const getSlotSelections = (slotIdx) => comboSlotSelections[slotIdx] || [];

  const selectComboSlotItem = async (slotIdx, product) => {
    // Check if this product needs configuration (sizes/modifiers)
    const hasSizes = !!product.size_group_id;
    const hasMods = (product.modifier_group_ids?.length > 0) || (product.modifier_groups?.length > 0);
    if (hasSizes || hasMods) {
      // Load product config and open sub-config
      try {
        const res = await api.get(`/products/${product.product_id}/config`);
        const configData = res.data;
        setComboItemConfig({ slotIdx, product, configData });
        setComboItemSelectedSize(null);
        setComboItemModifiers({});
        setComboItemModGroupIdx(0);
        if (configData.resolved_size_group?.sizes?.length > 0) {
          setComboItemConfigStep("size");
        } else if (configData.resolved_modifier_groups?.length > 0) {
          setComboItemConfigStep("modifiers");
        }
      } catch {
        toast.error("Failed to load item options");
      }
    } else {
      // Simple item, add directly to slot
      addItemToComboSlot(slotIdx, product, null, {}, 0);
    }
  };

  const addItemToComboSlot = (slotIdx, product, selectedSz, mods, modTotal) => {
    const selections = [...getSlotSelections(slotIdx)];
    const parts = [];
    if (selectedSz) parts.push(selectedSz.name);
    const allMods = [];
    for (const [gid, selMods] of Object.entries(mods)) {
      for (const m of selMods) {
        const placement = m.placement || "whole";
        const qty = m.qty || 1;
        let label = m.name;
        if (placement === "side_a") label = `${m.name} (A)`;
        else if (placement === "side_b") label = `${m.name} (B)`;
        if (qty > 1) label = `${qty}x ${label}`;
        parts.push(label);
        allMods.push({ name: m.name, placement, qty, modifier_id: m.modifier_id });
      }
    }
    selections.push({
      product_id: product.product_id,
      name: product.name,
      size_name: selectedSz?.name || "",
      modifiers: allMods,
      mod_total: modTotal,
      config_desc: parts.join(", "),
    });
    setComboSlotSelections((prev) => ({ ...prev, [slotIdx]: selections }));
    setComboItemConfig(null);
  };

  const removeComboSlotItem = (slotIdx, itemIdx) => {
    setComboSlotSelections((prev) => {
      const selections = [...(prev[slotIdx] || [])];
      selections.splice(itemIdx, 1);
      return { ...prev, [slotIdx]: selections };
    });
  };

  const getComboItemModPrice = (mod, selectedSz, configData) => {
    if (selectedSz && mod.size_offsets) {
      // size_offsets can be array or object
      if (Array.isArray(mod.size_offsets)) {
        const offset = mod.size_offsets.find((so) => so.size_id === selectedSz.size_id);
        if (offset) return parseFloat(offset.price) || 0;
      } else if (mod.size_offsets[selectedSz.size_id] !== undefined) {
        return parseFloat(mod.size_offsets[selectedSz.size_id]) || 0;
      }
    }
    return mod.price || 0;
  };

  const getComboItemHalfPrice = (mod, selectedSz, configData) => {
    return mod.split_half_price || Math.round(getComboItemModPrice(mod, selectedSz, configData) / 2 * 100) / 100;
  };

  const calcComboModTotal = (configData, mods, selectedSz) => {
    let modTotal = 0;
    const groups = configData?.resolved_modifier_groups || [];
    for (const g of groups) {
      const gid = g.modifier_group_id;
      const selected = mods[gid] || [];
      const freeAmt = g.free_amount || 0;
      selected.forEach((mod, idx) => {
        if (idx >= freeAmt) {
          const fullPrice = getComboItemModPrice(mod, selectedSz, configData);
          const halfPrice = getComboItemHalfPrice(mod, selectedSz, configData);
          const placement = mod.placement || "whole";
          const qty = mod.qty || 1;
          const unitPrice = placement === "whole" ? fullPrice : halfPrice;
          modTotal += unitPrice * qty;
        }
      });
    }
    return Math.round(modTotal * 100) / 100;
  };

  const confirmComboItemConfig = () => {
    if (!comboItemConfig) return;
    const { slotIdx, product, configData } = comboItemConfig;
    const modTotal = calcComboModTotal(configData, comboItemModifiers, comboItemSelectedSize);
    addItemToComboSlot(slotIdx, product, comboItemSelectedSize, comboItemModifiers, modTotal);
  };

  const getComboBasePrice = () => {
    if (!comboProduct) return 0;
    let basePrice = comboProduct.price || 0;
    if (comboSelectedSize) {
      const map = comboProduct.size_prices_map || {};
      const sizePrice = map[comboSelectedSize.size_id];
      if (sizePrice !== undefined && sizePrice !== null) basePrice = parseFloat(sizePrice);
    }
    return basePrice;
  };

  const getComboTotalPrice = () => {
    let base = getComboBasePrice();
    let modTotal = 0;
    for (const selections of Object.values(comboSlotSelections)) {
      for (const item of selections) modTotal += item.mod_total || 0;
    }
    return Math.round((base + modTotal) * 100) / 100;
  };

  const isComboComplete = () => {
    if (!comboProduct?.resolved_combo_slots) return false;
    for (let i = 0; i < comboProduct.resolved_combo_slots.length; i++) {
      const slot = comboProduct.resolved_combo_slots[i];
      const selections = comboSlotSelections[i] || [];
      if (selections.length < (slot.quantity || 1)) return false;
    }
    return true;
  };

  const addComboToCart = () => {
    if (!isComboComplete()) { toast.error("Please complete all combo selections"); return; }
    const price = getComboTotalPrice();
    const configId = `combo_${Date.now()}`;
    const parts = [];
    if (comboSelectedSize) parts.push(comboSelectedSize.name);
    const allSlotItems = [];
    for (let i = 0; i < (comboProduct.resolved_combo_slots || []).length; i++) {
      const slot = comboProduct.resolved_combo_slots[i];
      const selections = comboSlotSelections[i] || [];
      for (const item of selections) {
        let desc = item.name;
        if (item.config_desc) desc += ` (${item.config_desc})`;
        parts.push(desc);
        allSlotItems.push(item);
      }
    }
    setCart((prev) => [...prev, {
      product_id: comboProduct.product_id,
      name: comboProduct.name,
      price,
      quantity: 1,
      config_id: configId,
      is_combo: true,
      size_name: comboSelectedSize?.name || "",
      combo_items: allSlotItems,
      config_desc: parts.join(" + "),
    }]);
    setShowComboConfig(false);
    setComboProduct(null);
  };

  const updateQuantity = (productId, delta, configId) => {
    setCart((prev) => {
      return prev.map((i) => {
        const match = configId ? (i.config_id === configId) : (i.product_id === productId && !i.config_id);
        if (match) {
          const newQty = i.quantity + delta;
          return newQty > 0 ? { ...i, quantity: newQty } : null;
        }
        return i;
      }).filter(Boolean);
    });
  };

  const removeFromCart = (productId, configId) => setCart((prev) => prev.filter((i) => configId ? i.config_id !== configId : !(i.product_id === productId && !i.config_id)));

  const searchCustomers = async (q) => {
    setCustomerQuery(q);
    if (q.length < 2) { setCustomerResults([]); return; }
    try {
      const res = await api.get(`/customers?search=${encodeURIComponent(q)}&include_recent_orders=true`);
      setCustomerResults(res.data);
    } catch { /* silent */ }
  };

  const selectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setShowCustomerSearch(false);
    setShowLoyaltyScan(false);
    setCustomerQuery("");
    if (customer.customer_group_id) {
      try {
        const groups = await api.get("/customer-groups");
        const group = groups.data.find((g) => g.customer_group_id === customer.customer_group_id);
        if (group && group.is_active) setCustomerDiscount(group.discount_percent || 0);
        else setCustomerDiscount(0);
      } catch { setCustomerDiscount(0); }
    } else { setCustomerDiscount(0); }
  };

  const clearCustomer = () => { setSelectedCustomer(null); setCustomerDiscount(0); };

  const subtotal = cart.reduce((sum, i) => sum + i.price * i.quantity, 0);
  const taxRate = settings.tax_rate || 10;
  const customerDiscountAmount = customerDiscount > 0 ? subtotal * (customerDiscount / 100) : 0;
  const specialsDiscount = calcSpecialsDiscount();
  const totalDiscount = customerDiscountAmount + specialsDiscount;
  const afterDiscount = Math.max(0, subtotal - totalDiscount);

  // Calculate service fees
  const calcServiceFees = () => {
    let preTaxFees = 0;
    let postTaxFees = 0;
    let taxableFees = 0;
    for (const af of appliedFees) {
      if (af.waived) continue;
      const f = af.fee;
      const baseAmount = f.calc_threshold_pre_discount ? subtotal : afterDiscount;
      // Check thresholds
      if (f.min_threshold > 0 && baseAmount < f.min_threshold) continue;
      if (f.max_threshold > 0 && baseAmount > f.max_threshold) continue;
      let feeAmount = 0;
      if (f.fee_type === "percentage") {
        feeAmount = (f.application_type === "item") ? subtotal * (f.amount / 100) : afterDiscount * (f.amount / 100);
      } else {
        feeAmount = (f.application_type === "item") ? f.amount * cart.reduce((s, i) => s + i.quantity, 0) : f.amount;
      }
      if (f.apply_to === "pre_tax") { preTaxFees += feeAmount; }
      else { postTaxFees += feeAmount; }
      if (f.taxed) { taxableFees += feeAmount; }
    }
    return { preTaxFees, postTaxFees, taxableFees };
  };
  const { preTaxFees, postTaxFees, taxableFees } = calcServiceFees();
  const totalServiceFees = preTaxFees + postTaxFees;

  // Tax: on (afterDiscount + pre-tax fees). If a post-tax fee is also taxable, add tax on that.
  const baseTax = (afterDiscount + preTaxFees) * (taxRate / 100);
  const feeTax = appliedFees.filter((af) => !af.waived && af.fee.taxed && af.fee.apply_to === "post_tax").reduce((s, af) => {
    const f = af.fee;
    const baseAmount = f.calc_threshold_pre_discount ? subtotal : afterDiscount;
    if (f.min_threshold > 0 && baseAmount < f.min_threshold) return s;
    if (f.max_threshold > 0 && baseAmount > f.max_threshold) return s;
    let amt = f.fee_type === "percentage" ? afterDiscount * (f.amount / 100) : (f.application_type === "item" ? f.amount * cart.reduce((sum, i) => sum + i.quantity, 0) : f.amount);
    return s + amt * (taxRate / 100);
  }, 0);
  const totalTax = baseTax + feeTax;
  const total = afterDiscount + totalTax + totalServiceFees;
  const sym = settings.currency_symbol || "$";

  // Service fee helpers
  const handleWaiveFee = (af) => {
    if (af.fee.override_pin) {
      setWaiveFeeTarget(af);
      setWaivePinInput("");
      setShowWaivePin(true);
    } else {
      setAppliedFees((prev) => prev.map((f) => f.fee.service_fee_id === af.fee.service_fee_id ? { ...f, waived: !f.waived } : f));
    }
  };

  const confirmWaivePin = () => {
    if (!waiveFeeTarget) return;
    if (waivePinInput === waiveFeeTarget.fee.override_pin) {
      setAppliedFees((prev) => prev.map((f) => f.fee.service_fee_id === waiveFeeTarget.fee.service_fee_id ? { ...f, waived: !f.waived } : f));
      setShowWaivePin(false);
      toast.success("Fee waived");
    } else {
      toast.error("Incorrect PIN");
    }
  };

  const handleCheckout = async () => {
    if (cart.length === 0) return;
    setProcessing(true);
    try {
      const activeFees = appliedFees.filter((af) => !af.waived).map((af) => {
        const f = af.fee;
        let amt = f.fee_type === "percentage" ? afterDiscount * (f.amount / 100) : (f.application_type === "item" ? f.amount * cart.reduce((s, i) => s + i.quantity, 0) : f.amount);
        return { service_fee_id: f.service_fee_id, name: f.name, amount: Math.round(amt * 100) / 100, fee_type: f.fee_type, taxed: f.taxed };
      });
      const res = await api.post("/orders", {
        items: cart, subtotal: Math.round(subtotal * 100) / 100,
        tax: Math.round(totalTax * 100) / 100, discount: Math.round(totalDiscount * 100) / 100,
        total: Math.round(total * 100) / 100,
        service_fees: activeFees,
        service_fees_total: Math.round(totalServiceFees * 100) / 100,
        payment_method: paymentMethod, table_id: "", notes: "",
        customer_id: selectedCustomer?.customer_id || "",
        dining_type_id: selectedDiningType?.dining_type_id || "",
        scheduled_time: scheduledTime || "",
        applied_specials: appliedSpecials.map((a) => ({ special_id: a.special.special_id, name: a.special.name, amount: a.amount, type: a.special.discount_type })),
      });
      // Increment usage for code-based specials
      for (const applied of appliedSpecials) {
        if (applied.special.discount_code || applied.special.max_uses > 0) {
          api.post(`/specials/${applied.special.special_id}/increment-use`).catch(() => {});
        }
      }
      const ticketNum = res.data?.ticket_number || "";
      setLastTicket(ticketNum);
      toast.success(`Order ${ticketNum} completed!`);
      setCart([]);
      setShowCheckout(false);
      setSelectedCustomer(null);
      setCustomerDiscount(0);
      setAppliedSpecials([]);
      setAppliedFees([]);
      setScheduledTime("");
      fetchData();
    } catch (err) {
      toast.error("Order failed: " + (err.response?.data?.detail || "Unknown error"));
    } finally {
      setProcessing(false);
    }
  };

  // Quick-add customer
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickForm, setQuickForm] = useState({ first_name: "", last_name: "", phone: "", email: "" });
  const [quickSaving, setQuickSaving] = useState(false);

  const handleQuickAdd = async () => {
    if (!quickForm.first_name.trim() && !quickForm.phone.trim()) { toast.error("Name or phone required"); return; }
    setQuickSaving(true);
    try {
      const res = await api.post("/customers", {
        ...quickForm, customer_type: "individual", is_active: true,
        title: "", birthday: "", company_name: "", customer_group_id: "",
        ref_code: "", rewards_card: "", notes: "", gender: "decline",
        photo_url: "", license_number: "",
        marketing_email: "opt_out", marketing_phone: "opt_out", marketing_mail: "opt_out",
        addresses: [], house_account_enabled: false, house_account_balance: 0, house_account_max_limit: 0,
      });
      toast.success("Customer created!");
      selectCustomer(res.data);
      setShowQuickAdd(false);
      setShowCustomerSearch(false);
      setQuickForm({ first_name: "", last_name: "", phone: "", email: "" });
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to create"); }
    finally { setQuickSaving(false); }
  };

  // Time Clock handlers
  const handleClockInPin = async () => {
    if (clockPin.length < 1) return;
    setClockLoading(true);
    try {
      const res = await api.get(`/timeclock/status/${clockPin}`);
      const emp = res.data.employee;
      setClockEmployee(emp);
      if (res.data.clocked_in) {
        // Already clocked in -> show clock out
        setShowClockIn(false);
        setShowClockOut(true);
        setClockStep("pin");
      } else if (emp.roles_wages && emp.roles_wages.length > 1) {
        // Multiple roles -> ask which one
        setClockStep("role");
        setClockRoleSelect(emp.roles_wages[0]?.pos_role_id || "");
      } else {
        // Single role or none -> clock in directly
        const roleId = emp.roles_wages?.[0]?.pos_role_id || "";
        await api.post("/timeclock/clock-in", { pin: clockPin, pos_role_id: roleId });
        toast.success(`${emp.name || emp.first_name} clocked in!`);
        setShowClockIn(false);
        setClockPin("");
        setClockEmployee(null);
        setClockStep("pin");
        fetchData();
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clock-in failed");
    } finally { setClockLoading(false); }
  };

  const handleClockInWithRole = async () => {
    setClockLoading(true);
    try {
      await api.post("/timeclock/clock-in", { pin: clockPin, pos_role_id: clockRoleSelect });
      toast.success(`${clockEmployee?.name || "Employee"} clocked in!`);
      setShowClockIn(false);
      setClockPin("");
      setClockEmployee(null);
      setClockStep("pin");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clock-in failed");
    } finally { setClockLoading(false); }
  };

  const handleClockOut = async () => {
    setClockLoading(true);
    try {
      const res = await api.post("/timeclock/clock-out", { pin: clockPin, cash_tips_declared: parseFloat(clockTipAmount) || 0 });
      const hrs = res.data?.hours_worked || 0;
      toast.success(`${clockEmployee?.name || "Employee"} clocked out (${hrs.toFixed(1)} hrs)`);
      setShowClockOut(false);
      setClockPin("");
      setClockEmployee(null);
      setClockTipAmount("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Clock-out failed");
    } finally { setClockLoading(false); }
  };

  const isTippedRole = (emp) => {
    if (!emp?.roles_wages?.length) return false;
    // Check if any assigned role has tip_share
    return true; // We'll always show the option; the backend role determines it
  };

  const getCategoryName = (id) => categories.find((c) => c.category_id === id)?.name || "Uncategorized";

  const formatOrderDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diffMs = now - d;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  return (
    <div className="pos-layout pos-page-container pos-no-select" data-testid="pos-page">
      {/* Offline Banner */}
      {!isOnline && (
        <div className="offline-banner" data-testid="pos-offline-banner">
          <WifiOff className="inline h-4 w-4 mr-1.5 -mt-0.5" /> You are offline — orders will be saved when connection returns
        </div>
      )}
      {/* Left: Products */}
      <div className="flex flex-col h-screen overflow-hidden bg-white">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 bg-white">
          <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} data-testid="pos-back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="font-heading text-lg font-bold text-slate-900">POS Terminal</h1>
          {activeShifts.length > 0 && (
            <Badge variant="outline" className="text-[10px] text-emerald-600 border-emerald-300 bg-emerald-50">{activeShifts.length} on clock</Badge>
          )}
          {canInstall && (
            <Button variant="outline" size="sm" onClick={promptInstall} className="h-8 text-xs gap-1 text-blue-600 border-blue-200" data-testid="pos-install-btn">
              <Download className="h-3.5 w-3.5" /> Install App
            </Button>
          )}
          <div className="flex items-center gap-1 ml-auto">
            <Button variant="outline" size="sm" onClick={() => { setShowClockIn(true); setClockPin(""); setClockEmployee(null); setClockStep("pin"); }} className="h-8 text-xs gap-1" data-testid="pos-clock-btn">
              <LogIn className="h-3.5 w-3.5" /> Clock In / Out
            </Button>
            <div className="flex-1 max-w-xs relative ml-2">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input placeholder="Search products..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9 h-9" data-testid="pos-search-input" />
            </div>
          </div>
        </div>

        <div className="flex gap-2 px-4 py-3 border-b border-slate-100 overflow-x-auto" data-testid="pos-category-tabs">
          <button
            onClick={() => setSelectedCategory("all")}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === "all" ? "bg-[var(--brand-primary)] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
            data-testid="pos-category-all"
          >All</button>
          {categories.map((cat) => (
            <button
              key={cat.category_id}
              onClick={() => setSelectedCategory(cat.category_id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${selectedCategory === cat.category_id ? "text-white" : "text-slate-600 hover:bg-slate-200"}`}
              style={selectedCategory === cat.category_id ? { backgroundColor: cat.color } : { backgroundColor: "#F1F5F9" }}
              data-testid={`pos-category-${cat.category_id}`}
            >{cat.name}</button>
          ))}
        </div>

        <ScrollArea className="flex-1">
          {/* Store closed banner */}
          {!storeStatus.open && (
            <div className="bg-red-600 text-white px-4 py-3 flex items-center justify-center gap-2" data-testid="store-closed-banner">
              <Clock className="h-5 w-5" />
              <span className="font-bold text-sm">{storeStatus.reason || "Store is currently closed"}</span>
            </div>
          )}
          {storeStatus.open && storeStatus.minsLeft && storeStatus.minsLeft <= 30 && (
            <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-2" data-testid="store-closing-soon-banner">
              <Clock className="h-4 w-4" />
              <span className="font-semibold text-xs">Closing in {storeStatus.minsLeft} minutes (at {storeStatus.closesAt})</span>
            </div>
          )}
          <div className="pos-product-grid" data-testid="pos-product-grid">
            {filteredProducts.map((product) => {
              const stockInfo = outOfStock[product.product_id];
              const isBlocked = stockInfo && !stockInfo.allow_negative;
              const isWarning = stockInfo && stockInfo.allow_negative;
              return (
                <button key={product.product_id} className={`pos-product-card ${isBlocked ? "opacity-50 ring-2 ring-red-300" : ""}`} onClick={() => addToCart(product)} data-testid={`pos-product-${product.product_id}`}>
                  {product.image_url && (
                    <div className="w-full aspect-square rounded-md overflow-hidden mb-1.5 bg-slate-100">
                      <img src={product.image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
                    </div>
                  )}
                  <div className="text-xs text-slate-400 mb-0.5">
                    {getCategoryName(product.category_id)}
                    {product.is_combo && <Badge className="ml-1 text-[9px] bg-amber-100 text-amber-700 border-amber-200 py-0 px-1">COMBO</Badge>}
                    {isBlocked && <Badge className="ml-1 text-[9px] bg-red-100 text-red-700 border-red-200 py-0 px-1">OUT</Badge>}
                    {isWarning && <Badge className="ml-1 text-[9px] bg-amber-100 text-amber-600 border-amber-200 py-0 px-1">LOW</Badge>}
                  </div>
                  <div className="text-sm font-semibold text-slate-900 mb-0.5 line-clamp-2">{product.name}</div>
                  <div className="text-base font-bold text-[var(--brand-primary)]">{sym}{product.price.toFixed(2)}</div>
                </button>
              );
            })}
            {filteredProducts.length === 0 && (
              <div className="col-span-full text-center py-16 text-slate-400 text-sm">No products found</div>
            )}
          </div>
        </ScrollArea>
      </div>

      {/* Right: Cart */}
      <div className="pos-cart" data-testid="pos-cart">
        <div className="px-4 py-3 border-b border-slate-200 bg-white">
          <div className="flex items-center justify-between">
            <h2 className="font-heading text-base font-bold text-slate-900">Current Order</h2>
            <Badge variant="secondary" className="text-xs">{cart.length} items</Badge>
          </div>
          {/* Dining Type Selector */}
          {diningTypes.length > 0 && (
            <div className="flex gap-1.5 mt-2 overflow-x-auto" data-testid="pos-dining-type-selector">
              {diningTypes.map((dt) => {
                const dtStatus = getDiningTypeStatus(dt);
                return (
                  <button
                    key={dt.dining_type_id}
                    onClick={() => setSelectedDiningType(dt)}
                    className={`px-3 py-1.5 rounded-md text-xs font-semibold whitespace-nowrap transition-all flex items-center gap-1.5 ${
                      !dtStatus.available
                        ? "bg-red-50 text-red-400 border border-red-200 opacity-60"
                        : selectedDiningType?.dining_type_id === dt.dining_type_id
                        ? "bg-[var(--brand-primary)] text-white shadow-sm"
                        : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                    }`}
                    data-testid={`pos-dining-${dt.dining_type_id}`}
                  >
                    {selectedDiningType?.dining_type_id === dt.dining_type_id && <Check className="h-3 w-3" />}
                    {dt.short_name || dt.name}
                    {!dtStatus.available && <span className="text-[9px]">closed</span>}
                  </button>
                );
              })}
            </div>
          )}
          {/* Customer */}
          <div className="mt-2">
            {selectedCustomer ? (
              <div className="space-y-1.5">
                <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-lg px-3 py-1.5">
                  <UserCircle className="h-4 w-4 text-[var(--brand-primary)]" />
                  <span className="text-xs font-medium text-slate-800 flex-1 truncate" data-testid="pos-selected-customer-name">{selectedCustomer.first_name} {selectedCustomer.last_name}</span>
                  {customerDiscount > 0 && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200"><Percent className="h-3 w-3 mr-0.5" />{customerDiscount}%</Badge>}
                  <button onClick={clearCustomer} className="text-slate-400 hover:text-slate-600" data-testid="pos-clear-customer-btn"><X className="h-3.5 w-3.5" /></button>
                </div>
                {/* Customer Notes */}
                {selectedCustomer.notes && (
                  <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5" data-testid="pos-customer-notes">
                    <StickyNote className="h-3.5 w-3.5 text-amber-600 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-amber-800 leading-relaxed">{selectedCustomer.notes}</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => setShowCustomerSearch(true)} className="flex-1 text-left text-xs text-[var(--brand-primary)] hover:underline font-medium py-1" data-testid="pos-add-customer-btn">
                  + Add Customer
                </button>
                <button onClick={() => { setShowLoyaltyScan(true); setTimeout(() => loyaltyInputRef.current?.focus(), 100); }} className="text-xs text-slate-500 hover:text-[var(--brand-primary)] flex items-center gap-1 py-1 transition-colors" data-testid="pos-scan-loyalty-btn">
                  <ScanBarcode className="h-3.5 w-3.5" /> Scan Card
                </button>
              </div>
            )}
          </div>
        </div>

        <ScrollArea className="flex-1">
          <div className="p-4 space-y-2">
            {cart.length === 0 ? (
              <p className="text-center text-slate-400 text-sm py-16" data-testid="cart-empty">Tap products to add them</p>
            ) : (
              cart.map((item) => (
                <div key={item.config_id || item.product_id} className="flex items-center gap-3 bg-white p-3 rounded-lg border border-slate-100" data-testid={`cart-item-${item.config_id || item.product_id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-medium text-slate-900 truncate">{item.name}</p>
                      {item.is_combo && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200 py-0 px-1 flex-shrink-0">COMBO</Badge>}
                    </div>
                    {item.config_desc && <p className="text-[11px] text-slate-500 truncate">{item.config_desc}</p>}
                    <p className="text-xs text-slate-400">{sym}{item.price.toFixed(2)} each</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => updateQuantity(item.product_id, -1, item.config_id)} className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200" data-testid={`cart-minus-${item.config_id || item.product_id}`}>
                      <Minus className="h-3 w-3" />
                    </button>
                    <span className="w-8 text-center text-sm font-medium">{item.quantity}</span>
                    <button onClick={() => updateQuantity(item.product_id, 1, item.config_id)} className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200" data-testid={`cart-plus-${item.config_id || item.product_id}`}>
                      <Plus className="h-3 w-3" />
                    </button>
                    <button onClick={() => removeFromCart(item.product_id, item.config_id)} className="h-7 w-7 rounded bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100 ml-1" data-testid={`cart-remove-${item.config_id || item.product_id}`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        <div className="border-t border-slate-200 bg-white p-4 space-y-2" data-testid="cart-summary">
          {/* Specials & scheduled time buttons */}
          <div className="flex gap-1.5 mb-1">
            <button onClick={() => setShowSpecialsPanel(!showSpecialsPanel)} className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors" data-testid="specials-btn">
              <Sparkles className="h-3 w-3" /> Specials {activeSpecials.length > 0 && <Badge className="text-[9px] bg-amber-200 text-amber-800 py-0 px-1 ml-0.5">{activeSpecials.length}</Badge>}
            </button>
            <button onClick={() => { setShowCodeEntry(true); setDiscountCode(""); }} className="flex items-center justify-center gap-1 px-2 py-1.5 rounded-md text-xs font-medium bg-slate-50 text-slate-600 border border-slate-200 hover:bg-slate-100 transition-colors" data-testid="code-entry-btn">
              <Tag className="h-3 w-3" /> Code
            </button>
          </div>
          {/* Scheduled time */}
          <div className="flex items-center gap-1.5 mb-1">
            <Clock className="h-3 w-3 text-slate-400 flex-shrink-0" />
            <Input
              type="datetime-local"
              value={scheduledTime}
              onChange={(e) => setScheduledTime(e.target.value)}
              className="h-7 text-xs flex-1"
              data-testid="scheduled-time-input"
            />
            {scheduledTime && (
              <button onClick={() => setScheduledTime("")} className="h-5 w-5 rounded bg-slate-100 text-slate-500 flex items-center justify-center hover:bg-slate-200">
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
          {scheduledTime && (
            <div className="bg-blue-50 border border-blue-200 rounded-md px-2 py-1 mb-1">
              <p className="text-[10px] font-bold text-blue-700">SCHEDULED ORDER</p>
              <p className="text-xs text-blue-600">{new Date(scheduledTime).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</p>
            </div>
          )}

          {/* Applied specials list */}
          {appliedSpecials.length > 0 && (
            <div className="space-y-1">
              {appliedSpecials.map((applied) => (
                <div key={applied.special.special_id} className="flex items-center gap-1.5 bg-amber-50 border border-amber-200 rounded-md px-2 py-1">
                  <Sparkles className="h-3 w-3 text-amber-500 flex-shrink-0" />
                  <span className="text-xs font-medium text-amber-800 flex-1 truncate">{applied.special.display_name || applied.special.name}</span>
                  <span className="text-xs font-mono text-amber-700">
                    -{applied.special.discount_type === "percentage" ? `${applied.amount}%` : `${sym}${applied.amount.toFixed(2)}`}
                  </span>
                  <button onClick={() => removeSpecial(applied.special.special_id)} className="h-4 w-4 rounded bg-amber-200 text-amber-700 flex items-center justify-center hover:bg-amber-300" data-testid={`remove-special-${applied.special.special_id}`}>
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Specials panel (quick pick) */}
          {showSpecialsPanel && (
            <div className="border border-amber-200 rounded-lg p-2 bg-amber-50/50 space-y-1" data-testid="specials-panel">
              <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1">Active Specials</p>
              {activeSpecials.filter((s) => s.display_on_pos && !s.discount_code).map((s) => (
                <button
                  key={s.special_id}
                  onClick={() => applySpecial(s)}
                  disabled={appliedSpecials.some((a) => a.special.special_id === s.special_id)}
                  className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-xs transition-colors ${appliedSpecials.some((a) => a.special.special_id === s.special_id) ? "bg-emerald-100 text-emerald-700 border border-emerald-200" : "bg-white border border-amber-200 hover:bg-amber-100 text-slate-700"}`}
                  data-testid={`apply-special-${s.special_id}`}
                >
                  <Sparkles className="h-3 w-3 text-amber-500 flex-shrink-0" />
                  <span className="flex-1 truncate">{s.display_name || s.name}</span>
                  <span className="font-mono font-semibold">{s.discount_type === "percentage" ? `${s.discount_amount}%` : `${sym}${(s.discount_amount || 0).toFixed(2)}`}</span>
                  {appliedSpecials.some((a) => a.special.special_id === s.special_id) && <Check className="h-3 w-3 text-emerald-600" />}
                </button>
              ))}
              {activeSpecials.filter((s) => s.display_on_pos && !s.discount_code).length === 0 && (
                <p className="text-xs text-amber-600/60 text-center py-2">No active specials right now</p>
              )}
            </div>
          )}

          <div className="flex justify-between text-sm text-slate-500">
            <span>Subtotal</span><span>{sym}{subtotal.toFixed(2)}</span>
          </div>
          {customerDiscountAmount > 0 && (
            <div className="flex justify-between text-sm text-emerald-600">
              <span>Customer Discount ({customerDiscount}%)</span><span>-{sym}{customerDiscountAmount.toFixed(2)}</span>
            </div>
          )}
          {specialsDiscount > 0 && (
            <div className="flex justify-between text-sm text-amber-600">
              <span>Specials Discount</span><span>-{sym}{specialsDiscount.toFixed(2)}</span>
            </div>
          )}
          {/* Service Fees */}
          {appliedFees.length > 0 && (
            <div className="space-y-1" data-testid="applied-service-fees">
              {appliedFees.map((af) => {
                const f = af.fee;
                let feeAmt = 0;
                if (!af.waived) {
                  const baseAmount = f.calc_threshold_pre_discount ? subtotal : afterDiscount;
                  const thresholdOk = (f.min_threshold <= 0 || baseAmount >= f.min_threshold) && (f.max_threshold <= 0 || baseAmount <= f.max_threshold);
                  if (thresholdOk) {
                    feeAmt = f.fee_type === "percentage" ? afterDiscount * (f.amount / 100) : (f.application_type === "item" ? f.amount * cart.reduce((s, i) => s + i.quantity, 0) : f.amount);
                  }
                }
                return (
                  <div key={f.service_fee_id} className={`flex items-center justify-between text-sm ${af.waived ? "text-slate-400 line-through" : "text-slate-600"}`} data-testid={`applied-fee-${f.service_fee_id}`}>
                    <div className="flex items-center gap-1">
                      <Receipt className="h-3 w-3 flex-shrink-0" />
                      <span className="truncate max-w-[140px]">{f.name}</span>
                      <button onClick={() => handleWaiveFee(af)} className={`text-[10px] px-1 py-0.5 rounded ${af.waived ? "bg-emerald-100 text-emerald-700" : "bg-red-50 text-red-600 hover:bg-red-100"}`} data-testid={`waive-fee-${f.service_fee_id}`}>
                        {af.waived ? "Restore" : "Waive"}
                      </button>
                    </div>
                    <span>{af.waived ? sym + "0.00" : sym + feeAmt.toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
          )}
          <div className="flex justify-between text-sm text-slate-500">
            <span>Tax ({taxRate}%)</span><span>{sym}{totalTax.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-lg font-bold text-slate-900 border-t border-slate-100 pt-3">
            <span>Total</span><span data-testid="cart-total">{sym}{total.toFixed(2)}</span>
          </div>
          <div className="flex gap-2">
            {cart.length > 0 && (
              <Button variant="outline" onClick={() => setCart([])} className="flex-shrink-0" data-testid="cart-clear-btn">
                <X className="h-4 w-4" />
              </Button>
            )}
            <Button
              className="flex-1 h-12 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white text-base font-semibold"
              disabled={cart.length === 0}
              onClick={() => setShowCheckout(true)}
              data-testid="checkout-btn"
            >
              Charge {sym}{total.toFixed(2)}
            </Button>
          </div>
        </div>
      </div>

      {/* Checkout Dialog */}
      <Dialog open={showCheckout} onOpenChange={setShowCheckout}>
        <DialogContent className="sm:max-w-md" data-testid="checkout-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Complete Payment</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Select a payment method to complete the order</DialogDescription>
          </DialogHeader>
          <div className="text-center py-4">
            <p className="text-3xl font-heading font-bold text-slate-900">{sym}{total.toFixed(2)}</p>
            <p className="text-sm text-slate-500 mt-1">{cart.length} items</p>
            {selectedDiningType && (
              <Badge className="mt-2 text-xs bg-slate-100 text-slate-700 border-slate-200" data-testid="checkout-dining-badge">
                <UtensilsCrossed className="h-3 w-3 mr-1" /> {selectedDiningType.name} ({selectedDiningType.short_name})
              </Badge>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={() => setPaymentMethod("cash")}
              className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-colors ${paymentMethod === "cash" ? "border-[var(--brand-primary)] bg-blue-50" : "border-slate-200"}`}
              data-testid="payment-cash-btn"
            >
              <Banknote className="h-6 w-6 text-emerald-600" />
              <span className="text-sm font-medium">Cash</span>
            </button>
            <button
              onClick={() => setPaymentMethod("card")}
              className={`p-4 rounded-lg border-2 flex flex-col items-center gap-2 transition-colors ${paymentMethod === "card" ? "border-[var(--brand-primary)] bg-blue-50" : "border-slate-200"}`}
              data-testid="payment-card-btn"
            >
              <CreditCard className="h-6 w-6 text-blue-600" />
              <span className="text-sm font-medium">Card</span>
            </button>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCheckout(false)} data-testid="checkout-cancel-btn">Cancel</Button>
            <Button
              onClick={handleCheckout} disabled={processing}
              className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
              data-testid="checkout-confirm-btn"
            >
              {processing ? "Processing..." : "Complete Order"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Search Dialog */}

      {/* PIN Override Dialog for Service Fee Waive */}
      <Dialog open={showWaivePin} onOpenChange={setShowWaivePin}>
        <DialogContent className="sm:max-w-xs" data-testid="waive-pin-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><ShieldCheck className="h-5 w-5 text-amber-500" /> Manager PIN Required</DialogTitle>
            <DialogDescription>Enter the override PIN to {waiveFeeTarget?.waived ? "restore" : "waive"} "{waiveFeeTarget?.fee?.name}"</DialogDescription>
          </DialogHeader>
          <Input type="password" value={waivePinInput} onChange={(e) => setWaivePinInput(e.target.value)} placeholder="Enter PIN" className="text-center text-lg tracking-widest" autoFocus data-testid="waive-pin-input"
            onKeyDown={(e) => { if (e.key === "Enter") confirmWaivePin(); }} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowWaivePin(false)}>Cancel</Button>
            <Button onClick={confirmWaivePin} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="waive-pin-confirm-btn">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Customer Search Dialog (existing) */}
      <Dialog open={showCustomerSearch} onOpenChange={(open) => { setShowCustomerSearch(open); if (!open) { setShowQuickAdd(false); setCustomerQuery(""); setCustomerResults([]); } }}>
        <DialogContent className="sm:max-w-md" data-testid="customer-search-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{showQuickAdd ? "New Customer" : "Find Customer"}</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {showQuickAdd ? "Quick-add a new customer" : "Search by name, phone, email, or rewards card"}
            </DialogDescription>
          </DialogHeader>

          {!showQuickAdd ? (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input placeholder="Name, phone, email, or card #..." value={customerQuery} onChange={(e) => searchCustomers(e.target.value)} className="pl-9" autoFocus data-testid="pos-customer-search-input" />
              </div>
              <ScrollArea className="max-h-[320px]">
                <div className="space-y-1">
                  {customerResults.map((c) => (
                    <button key={c.customer_id} onClick={() => selectCustomer(c)} className="w-full flex items-start gap-3 p-3 rounded-lg hover:bg-slate-50 text-left transition-colors" data-testid={`pos-select-customer-${c.customer_id}`}>
                      <div className="h-8 w-8 rounded-full bg-[var(--brand-primary)] text-white flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">
                        {(c.first_name?.[0] || "")}{(c.last_name?.[0] || "")}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-slate-900 truncate">{c.first_name} {c.last_name}</p>
                          {c.customer_group_id && <Badge variant="outline" className="text-[10px] flex-shrink-0">Group</Badge>}
                        </div>
                        <p className="text-xs text-slate-400 truncate">{[c.phone, c.email].filter(Boolean).join(" | ")}</p>
                        {c.rewards_card && (
                          <p className="text-xs text-slate-400 truncate flex items-center gap-1 mt-0.5"><ScanBarcode className="h-3 w-3" /> {c.rewards_card}</p>
                        )}
                        {/* Recent Orders */}
                        {c.recent_orders && c.recent_orders.length > 0 && (
                          <div className="mt-1.5 pt-1.5 border-t border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-1 flex items-center gap-1"><Clock className="h-3 w-3" /> Recent Orders</p>
                            {c.recent_orders.slice(0, 2).map((order) => (
                              <div key={order.order_id} className="flex items-center justify-between text-[11px] text-slate-500 py-0.5">
                                <span className="truncate flex-1 mr-2">{order.items?.map(i => i.name).join(", ") || "Order"}</span>
                                <span className="flex-shrink-0 font-medium text-slate-600">{sym}{(order.total || 0).toFixed(2)} · {formatOrderDate(order.created_at)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {c.notes && (
                          <p className="text-[11px] text-amber-700 mt-1 flex items-center gap-1 truncate"><StickyNote className="h-3 w-3 flex-shrink-0" /> {c.notes}</p>
                        )}
                      </div>
                    </button>
                  ))}
                  {customerQuery.length >= 2 && customerResults.length === 0 && (
                    <div className="text-center py-6">
                      <p className="text-sm text-slate-400 mb-3">No customers found for "{customerQuery}"</p>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setShowQuickAdd(true);
                          const q = customerQuery.trim();
                          if (q.includes("@")) setQuickForm({ first_name: "", last_name: "", phone: "", email: q });
                          else if (/^\d/.test(q)) setQuickForm({ first_name: "", last_name: "", phone: q, email: "" });
                          else {
                            const parts = q.split(" ");
                            setQuickForm({ first_name: parts[0] || "", last_name: parts.slice(1).join(" ") || "", phone: "", email: "" });
                          }
                        }}
                        className="text-[var(--brand-primary)] border-[var(--brand-primary)]"
                        data-testid="pos-quick-add-btn"
                      >
                        <Plus className="h-4 w-4 mr-1" /> Create New Customer
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
              {customerQuery.length < 2 && (
                <div className="border-t border-slate-100 pt-3">
                  <Button variant="ghost" onClick={() => { setShowQuickAdd(true); setQuickForm({ first_name: "", last_name: "", phone: "", email: "" }); }} className="w-full text-[var(--brand-primary)] hover:bg-blue-50" data-testid="pos-new-customer-btn">
                    <Plus className="h-4 w-4 mr-1" /> New Customer
                  </Button>
                </div>
              )}
            </>
          ) : (
            <>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">First Name</Label>
                    <Input value={quickForm.first_name} onChange={(e) => setQuickForm({ ...quickForm, first_name: e.target.value })} autoFocus data-testid="quick-first-name" />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Last Name</Label>
                    <Input value={quickForm.last_name} onChange={(e) => setQuickForm({ ...quickForm, last_name: e.target.value })} data-testid="quick-last-name" />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Phone</Label>
                  <Input value={quickForm.phone} onChange={(e) => setQuickForm({ ...quickForm, phone: e.target.value })} placeholder="Phone number" data-testid="quick-phone" />
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Email</Label>
                  <Input type="email" value={quickForm.email} onChange={(e) => setQuickForm({ ...quickForm, email: e.target.value })} placeholder="Email address" data-testid="quick-email" />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowQuickAdd(false)} data-testid="quick-back-btn">Back to Search</Button>
                <Button onClick={handleQuickAdd} disabled={quickSaving} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="quick-save-btn">
                  {quickSaving ? "Creating..." : "Create & Select"}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Loyalty Card Scan Dialog */}
      <Dialog open={showLoyaltyScan} onOpenChange={setShowLoyaltyScan}>
        <DialogContent className="sm:max-w-sm" data-testid="loyalty-scan-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><ScanBarcode className="h-5 w-5 text-[var(--brand-primary)]" /> Scan Loyalty Card</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">Scan a barcode or type the card number manually</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-6 bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg text-center">
              <ScanBarcode className="h-10 w-10 text-slate-400 mx-auto mb-2" />
              <p className="text-sm text-slate-500">Scan barcode now...</p>
              <p className="text-xs text-slate-400 mt-1">Scanner input will be captured automatically</p>
            </div>
            <div className="relative">
              <Input
                ref={loyaltyInputRef}
                placeholder="Or type card number..."
                value={loyaltyInput}
                onChange={(e) => setLoyaltyInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleLoyaltyManualSearch(); }}
                data-testid="loyalty-card-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLoyaltyScan(false)}>Cancel</Button>
            <Button
              onClick={handleLoyaltyManualSearch}
              disabled={loyaltyInput.trim().length < 2}
              className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
              data-testid="loyalty-search-btn"
            >
              Look Up
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clock In Dialog */}
      <Dialog open={showClockIn} onOpenChange={(open) => { setShowClockIn(open); if (!open) { setClockPin(""); setClockEmployee(null); setClockStep("pin"); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="clock-in-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><LogIn className="h-5 w-5 text-[var(--brand-primary)]" /> Clock In / Out</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {clockStep === "pin" ? "Enter your employee PIN" : "Select your role for this shift"}
            </DialogDescription>
          </DialogHeader>

          {clockStep === "pin" && (
            <div className="space-y-4">
              <Input
                type="password" placeholder="Enter PIN..." value={clockPin}
                onChange={(e) => setClockPin(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") handleClockInPin(); }}
                autoFocus maxLength={6}
                className="text-center text-2xl tracking-[0.5em] h-14 font-mono"
                data-testid="clock-pin-input"
              />
              <Button onClick={handleClockInPin} disabled={clockLoading || clockPin.length < 1} className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white h-11" data-testid="clock-pin-submit">
                {clockLoading ? "Checking..." : "Submit PIN"}
              </Button>
            </div>
          )}

          {clockStep === "role" && clockEmployee && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                <p className="text-sm font-semibold text-slate-800">{clockEmployee.name || `${clockEmployee.first_name} ${clockEmployee.last_name}`}</p>
                <p className="text-xs text-slate-500">Select your role for this shift</p>
              </div>
              <Select value={clockRoleSelect} onValueChange={setClockRoleSelect}>
                <SelectTrigger data-testid="clock-role-select"><SelectValue placeholder="Select Role" /></SelectTrigger>
                <SelectContent>
                  {(clockEmployee.roles_wages || []).map((rw, idx) => (
                    <SelectItem key={idx} value={rw.pos_role_id}>{rw.pos_role_id}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={handleClockInWithRole} disabled={clockLoading || !clockRoleSelect} className="w-full bg-emerald-600 hover:bg-emerald-700 text-white h-11" data-testid="clock-role-submit">
                {clockLoading ? "Clocking In..." : "Clock In"}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Clock Out Dialog */}
      <Dialog open={showClockOut} onOpenChange={(open) => { setShowClockOut(open); if (!open) { setClockPin(""); setClockEmployee(null); setClockTipAmount(""); } }}>
        <DialogContent className="sm:max-w-sm" data-testid="clock-out-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><LogOut className="h-5 w-5 text-red-500" /> Clock Out</DialogTitle>
            <DialogDescription className="text-sm text-slate-500">
              {clockEmployee?.name || "Employee"} is clocking out
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
              <p className="text-sm font-semibold text-slate-800">{clockEmployee?.name || `${clockEmployee?.first_name || ""} ${clockEmployee?.last_name || ""}`}</p>
            </div>
            <div>
              <Label className="text-xs text-slate-500 mb-1 block">Declare Cash Tips</Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="number" step="0.01" placeholder="0.00" value={clockTipAmount}
                  onChange={(e) => setClockTipAmount(e.target.value)}
                  className="pl-9 h-12 text-lg"
                  data-testid="clock-tip-input"
                />
              </div>
              <p className="text-xs text-slate-400 mt-1">Enter cash tips received during this shift (credit card tips are automatic)</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => { setClockTipAmount("0"); handleClockOut(); }} className="flex-1" data-testid="clock-out-no-tips">No Tips</Button>
              <Button onClick={handleClockOut} disabled={clockLoading} className="flex-1 bg-red-600 hover:bg-red-700 text-white" data-testid="clock-out-submit">
                {clockLoading ? "Processing..." : "Clock Out"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Item Configuration Dialog (Size + Modifiers) */}
      <Dialog open={showItemConfig} onOpenChange={(open) => { if (!open) { setShowItemConfig(false); setConfigProduct(null); } }}>
        <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col" data-testid="item-config-dialog">
          {configLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>
          ) : configProduct && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg">{configProduct.name}</DialogTitle>
                <DialogDescription asChild>
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <span className="font-semibold text-[var(--brand-primary)]">{sym}{getConfiguredPrice().toFixed(2)}</span>
                    {selectedSize && <Badge variant="outline" className="text-[10px]">{selectedSize.name}</Badge>}
                    {configStep === "modifiers" && configProduct.resolved_modifier_groups?.length > 1 && (
                      <span className="text-xs text-slate-400 ml-auto">Step {currentModGroupIdx + 1} of {configProduct.resolved_modifier_groups.length}</span>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>

              <ScrollArea className="flex-1 -mx-6 px-6">
                {/* SIZE SELECTION */}
                {configStep === "size" && configProduct.resolved_size_group && (
                  <div className="space-y-3" data-testid="config-size-step">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Size</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {(configProduct.resolved_size_group.sizes || []).map((size) => {
                        const sizePrice = configProduct.size_prices_map?.[size.size_id];
                        const isSelected = selectedSize?.size_id === size.size_id;
                        return (
                          <button
                            key={size.size_id}
                            onClick={() => handleSizeSelect(size)}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${
                              isSelected ? "border-[var(--brand-primary)] bg-blue-50 shadow-sm" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"
                            }`}
                            data-testid={`config-size-${size.size_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">{size.name}</p>
                                <p className="text-xs text-slate-400">{size.short_name}</p>
                              </div>
                              {isSelected && <Check className="h-5 w-5 text-[var(--brand-primary)]" />}
                            </div>
                            {sizePrice !== undefined && sizePrice !== null && (
                              <p className="text-base font-bold text-[var(--brand-primary)] mt-1">{sym}{parseFloat(sizePrice).toFixed(2)}</p>
                            )}
                            {/* Size included items */}
                            {(configProduct.size_included_items?.[size.size_id] || []).length > 0 && (
                              <div className="mt-1">
                                {(configProduct.size_included_items[size.size_id]).map((inc, i) => (
                                  <p key={i} className="text-[10px] text-emerald-600">Includes: {inc.quantity > 1 ? `${inc.quantity}x ` : ""}{inc.name}</p>
                                ))}
                              </div>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* MODIFIER SELECTION */}
                {configStep === "modifiers" && configProduct.resolved_modifier_groups?.length > 0 && (() => {
                  const group = configProduct.resolved_modifier_groups[currentModGroupIdx];
                  if (!group) return null;
                  const gid = group.modifier_group_id;
                  const selected = selectedModifiers[gid] || [];
                  const min = group.min_select || group.min_selections || 0;
                  const max = group.max_select || group.max_selections || 99;
                  const freeAmt = group.free_amount || 0;
                  // Filter modifiers by size availability
                  const sizeAvail = configProduct.modifier_size_availability || {};
                  const availableModifiers = (group.modifiers || []).filter((mod) => {
                    const allowed = sizeAvail[mod.modifier_id];
                    if (!allowed) return true; // No restriction = available for all sizes
                    if (!selectedSize) return true; // No size selected = show all
                    return allowed.includes(selectedSize.size_id);
                  });
                  return (
                    <div className="space-y-3" data-testid="config-mod-step">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900">{group.name}</h3>
                          <p className="text-xs text-slate-400">
                            {min > 0 ? `Select ${min}` : "Optional"}{max < 99 ? ` (max ${max})` : ""}
                            {freeAmt > 0 ? ` · First ${freeAmt} free` : ""}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">{selected.length}{max < 99 ? `/${max}` : ""} selected</Badge>
                      </div>
                      {/* Size change button */}
                      {selectedSize && (
                        <button onClick={handleSizeChange} className="text-xs text-[var(--brand-primary)] hover:underline flex items-center gap-1" data-testid="config-change-size">
                          <RotateCcw className="h-3 w-3" /> Change size ({selectedSize.name})
                        </button>
                      )}
                      <div className="space-y-1.5">
                        {availableModifiers.map((mod) => {
                          const isSelected = selected.some((s) => s.modifier_id === mod.modifier_id);
                          const selectedMod = selected.find((s) => s.modifier_id === mod.modifier_id);
                          const modPrice = getModPrice(mod);
                          const halfPrice = getHalfPrice(mod);
                          const selIdx = isSelected ? selected.indexOf(selectedMod) : -1;
                          // Per-modifier free_quantity overrides group free_amount
                          const modFreeQty = mod.free_quantity != null ? mod.free_quantity : 0;
                          const currentQty = isSelected ? (selectedMod.qty || 1) : 0;
                          const isFreeByMod = modFreeQty > 0 && currentQty <= modFreeQty;
                          const isFreeByGroup = !isFreeByMod && isSelected && selIdx < freeAmt;
                          const isFree = isFreeByMod || isFreeByGroup;
                          const willBeFreeByGroup = !isSelected && selected.length < freeAmt && freeAmt > 0;
                          const willBeFreeByMod = !isSelected && modFreeQty > 0;
                          const willBeFree = willBeFreeByGroup || willBeFreeByMod;
                          const showFree = isFree || willBeFree;
                          const hasSplit = mod.allow_split === true;
                          const linePrice = isSelected ? calcModLinePrice(mod, selectedMod) : 0;
                          return (
                            <div
                              key={mod.modifier_id}
                              className={`rounded-lg border-2 transition-all ${
                                isSelected ? "border-[var(--brand-primary)] bg-blue-50" : "border-slate-200 hover:border-slate-300"
                              }`}
                              data-testid={`config-mod-${mod.modifier_id}`}
                            >
                              {/* Main toggle row */}
                              <button
                                onClick={() => toggleModifier(group, mod)}
                                className="w-full p-3 flex items-center justify-between text-left"
                                disabled={!isSelected && selected.length >= max}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                                    isSelected ? "bg-[var(--brand-primary)] border-[var(--brand-primary)]" : "border-slate-300"
                                  }`}>
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  {mod.image_url && <img src={mod.image_url} alt="" className="h-8 w-8 rounded object-cover border border-slate-200 flex-shrink-0" loading="lazy" />}
                                  <span className="text-sm font-medium text-slate-900">{mod.name}</span>
                                  {mod.is_product_default && <Badge className="text-[9px] bg-amber-100 text-amber-700 border-amber-200">Default</Badge>}
                                  {isSelected && selectedMod.qty > 1 && <Badge className="text-[10px] bg-[var(--brand-primary)] text-white">x{selectedMod.qty}</Badge>}
                                </div>
                                <div className="flex items-center gap-1.5">
                                  {modPrice > 0 && <span className={`text-sm font-semibold ${isFree || willBeFree ? "text-slate-400 line-through" : "text-slate-700"}`}>+{sym}{(isSelected ? linePrice : modPrice).toFixed(2)}</span>}
                                  {modPrice === 0 && !isFree && !willBeFree && <span className="text-sm text-slate-400">Included</span>}
                                  {(isFree || willBeFree) && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">FREE</Badge>}
                                </div>
                              </button>
                              {/* Placement + Qty controls when selected */}
                              {isSelected && (
                                <div className="px-3 pb-3 space-y-2">
                                  {/* Whole / Side A / Side B */}
                                  {hasSplit && (
                                    <div className="flex gap-1.5" data-testid={`mod-placement-${mod.modifier_id}`}>
                                      {[
                                        { id: "whole", label: "Whole", price: modPrice },
                                        { id: "side_a", label: "Side A", price: halfPrice },
                                        { id: "side_b", label: "Side B", price: halfPrice },
                                      ].map((p) => (
                                        <button
                                          key={p.id}
                                          onClick={(e) => { e.stopPropagation(); updateModPlacement(group, mod.modifier_id, p.id); }}
                                          className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${
                                            (selectedMod.placement || "whole") === p.id
                                              ? "bg-[var(--brand-primary)] text-white shadow-sm"
                                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                                          }`}
                                          data-testid={`mod-${p.id}-${mod.modifier_id}`}
                                        >
                                          {p.label}
                                          <span className="block text-[10px] opacity-75">{sym}{p.price.toFixed(2)}</span>
                                        </button>
                                      ))}
                                    </div>
                                  )}
                                  {/* Qty controls */}
                                  <div className="flex items-center justify-between" data-testid={`mod-qty-${mod.modifier_id}`}>
                                    <span className="text-xs text-slate-500">Quantity{mod.max_quantity ? ` (max ${mod.max_quantity})` : " (extra)"}</span>
                                    <div className="flex items-center gap-1.5">
                                      <button
                                        onClick={(e) => { e.stopPropagation(); updateModQty(group, mod.modifier_id, -1); }}
                                        className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200 text-slate-600"
                                        data-testid={`mod-qty-minus-${mod.modifier_id}`}
                                      ><Minus className="h-3 w-3" /></button>
                                      <span className="w-6 text-center text-sm font-bold text-slate-900">{selectedMod.qty || 1}</span>
                                      <button
                                        onClick={(e) => { e.stopPropagation(); updateModQty(group, mod.modifier_id, 1); }}
                                        className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200 text-slate-600"
                                        data-testid={`mod-qty-plus-${mod.modifier_id}`}
                                      ><Plus className="h-3 w-3" /></button>
                                    </div>
                                  </div>
                                  {/* Included Items (Comes With) */}
                                  {mod.included_items?.length > 0 && (() => {
                                    const inclItems = getIncludedQty(mod, selectedSize?.size_id);
                                    if (inclItems.length === 0) return null;
                                    return (
                                      <div className="bg-amber-50 border border-amber-200 rounded-md p-2 space-y-1.5" data-testid={`mod-included-${mod.modifier_id}`}>
                                        <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider flex items-center gap-1">
                                          <Package className="h-3 w-3" /> Comes With
                                        </p>
                                        {inclItems.map((inc) => {
                                          const extraQty = includedExtras[mod.modifier_id]?.[inc.product_id] || 0;
                                          const extraPrice = getIncludedExtraPrice(inc);
                                          const maxExtra = inc.max_extra ?? 5;
                                          return (
                                            <div key={inc.product_id} className="flex items-center justify-between gap-2" data-testid={`included-${mod.modifier_id}-${inc.product_id}`}>
                                              <div className="flex-1 min-w-0">
                                                <span className="text-xs font-medium text-slate-800">{inc.included_qty}x {inc.name}</span>
                                                {extraQty > 0 && <span className="text-[10px] text-amber-600 ml-1">+{extraQty} extra ({sym}{(extraPrice * extraQty).toFixed(2)})</span>}
                                              </div>
                                              <div className="flex items-center gap-1">
                                                {extraQty > 0 && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setIncludedExtras((prev) => {
                                                        const modExtras = { ...(prev[mod.modifier_id] || {}) };
                                                        modExtras[inc.product_id] = Math.max(0, extraQty - 1);
                                                        return { ...prev, [mod.modifier_id]: modExtras };
                                                      });
                                                    }}
                                                    className="h-6 w-6 rounded bg-white border border-amber-300 flex items-center justify-center hover:bg-amber-100 text-amber-700"
                                                    data-testid={`included-minus-${mod.modifier_id}-${inc.product_id}`}
                                                  ><Minus className="h-2.5 w-2.5" /></button>
                                                )}
                                                {extraQty > 0 && <span className="w-4 text-center text-[10px] font-bold text-amber-700">+{extraQty}</span>}
                                                {extraQty < maxExtra && (
                                                  <button
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      setIncludedExtras((prev) => {
                                                        const modExtras = { ...(prev[mod.modifier_id] || {}) };
                                                        modExtras[inc.product_id] = (modExtras[inc.product_id] || 0) + 1;
                                                        return { ...prev, [mod.modifier_id]: modExtras };
                                                      });
                                                    }}
                                                    className="h-6 rounded-md bg-white border border-amber-300 flex items-center justify-center hover:bg-amber-100 text-amber-700 px-1.5 gap-0.5"
                                                    data-testid={`included-plus-${mod.modifier_id}-${inc.product_id}`}
                                                  >
                                                    <Plus className="h-2.5 w-2.5" />
                                                    <span className="text-[10px] font-medium">{sym}{extraPrice.toFixed(2)}</span>
                                                  </button>
                                                )}
                                              </div>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    );
                                  })()}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </ScrollArea>

              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                {configStep === "modifiers" && (
                  <Button variant="outline" onClick={prevModGroup} data-testid="config-prev-btn">
                    Back
                  </Button>
                )}
                <div className="flex-1" />
                {configStep === "size" && (
                  <Button
                    onClick={() => {
                      if (!selectedSize) { toast.error("Please select a size"); return; }
                      if (configProduct.resolved_modifier_groups?.length > 0) {
                        setConfigStep("modifiers");
                      } else {
                        addConfiguredItem();
                      }
                    }}
                    disabled={!selectedSize}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
                    data-testid="config-next-btn"
                  >
                    {configProduct.resolved_modifier_groups?.length > 0 ? "Next" : "Add to Order"} <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                {configStep === "modifiers" && (
                  <Button
                    onClick={nextModGroup}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
                    data-testid="config-add-btn"
                  >
                    {currentModGroupIdx < (configProduct.resolved_modifier_groups?.length || 1) - 1 ? (
                      <>Next <ChevronRight className="h-4 w-4 ml-1" /></>
                    ) : (
                      <>Add to Order · {sym}{getConfiguredPrice().toFixed(2)}</>
                    )}
                  </Button>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Combo Meal Configuration Dialog */}
      <Dialog open={showComboConfig} onOpenChange={(open) => { if (!open) { setShowComboConfig(false); setComboProduct(null); setComboItemConfig(null); } }}>
        <DialogContent className="sm:max-w-xl max-h-[85vh] flex flex-col" data-testid="combo-config-dialog">
          {comboLoading ? (
            <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-2 border-[var(--brand-primary)] border-t-transparent" /></div>
          ) : comboProduct && !comboItemConfig ? (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg flex items-center gap-2">
                  <Package className="h-5 w-5 text-amber-600" /> {comboProduct.name}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <span className="font-semibold text-[var(--brand-primary)]">{sym}{getComboTotalPrice().toFixed(2)}</span>
                    {comboSelectedSize && <Badge variant="outline" className="text-[10px]">{comboSelectedSize.name}</Badge>}
                    <Badge className="text-[10px] bg-amber-100 text-amber-700 border-amber-200">COMBO</Badge>
                  </div>
                </DialogDescription>
              </DialogHeader>

              <ScrollArea className="flex-1 -mx-6 px-6">
                {/* COMBO SIZE SELECTION */}
                {comboStep === "size" && comboProduct.resolved_size_group && (
                  <div className="space-y-3" data-testid="combo-size-step">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Combo Size</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {(comboProduct.resolved_size_group.sizes || []).map((size) => {
                        const sizePrice = comboProduct.size_prices_map?.[size.size_id];
                        const isSelected = comboSelectedSize?.size_id === size.size_id;
                        return (
                          <button
                            key={size.size_id}
                            onClick={() => handleComboSizeSelect(size)}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${isSelected ? "border-[var(--brand-primary)] bg-blue-50 shadow-sm" : "border-slate-200 hover:border-slate-300 hover:bg-slate-50"}`}
                            data-testid={`combo-size-${size.size_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">{size.name}</p>
                                <p className="text-xs text-slate-400">{size.short_name}</p>
                              </div>
                              {isSelected && <Check className="h-5 w-5 text-[var(--brand-primary)]" />}
                            </div>
                            {sizePrice !== undefined && sizePrice !== null && (
                              <p className="text-base font-bold text-[var(--brand-primary)] mt-1">{sym}{parseFloat(sizePrice).toFixed(2)}</p>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* COMBO SLOT SELECTIONS */}
                {comboStep === "slots" && (
                  <div className="space-y-4" data-testid="combo-slots-step">
                    {(comboProduct.resolved_combo_slots || []).map((slot, slotIdx) => {
                      const selections = getSlotSelections(slotIdx);
                      const needed = slot.quantity || 1;
                      const isComplete = selections.length >= needed;
                      const isCurrent = slotIdx === comboCurrentSlotIdx;
                      return (
                        <div key={slot.slot_id || slotIdx} className={`border-2 rounded-lg overflow-hidden transition-all ${isCurrent ? "border-[var(--brand-primary)] shadow-sm" : isComplete ? "border-emerald-300 bg-emerald-50/30" : "border-slate-200"}`} data-testid={`combo-slot-config-${slotIdx}`}>
                          <button
                            onClick={() => setComboCurrentSlotIdx(slotIdx)}
                            className={`w-full flex items-center gap-3 px-4 py-3 text-left ${isCurrent ? "bg-blue-50" : isComplete ? "bg-emerald-50/50" : "bg-slate-50"}`}
                          >
                            <div className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${isComplete ? "bg-emerald-500 text-white" : isCurrent ? "bg-[var(--brand-primary)] text-white" : "bg-slate-300 text-white"}`}>
                              {isComplete ? <Check className="h-4 w-4" /> : slotIdx + 1}
                            </div>
                            <div className="flex-1">
                              <p className="text-sm font-semibold text-slate-900">{slot.slot_name}</p>
                              <p className="text-xs text-slate-400">Pick {needed} item{needed !== 1 ? "s" : ""} · {selections.length}/{needed} selected</p>
                            </div>
                            {isComplete && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">Done</Badge>}
                          </button>

                          {/* Expanded slot - show eligible products */}
                          {isCurrent && (
                            <div className="p-3 border-t border-slate-200 space-y-2">
                              {/* Already selected items for this slot */}
                              {selections.length > 0 && (
                                <div className="space-y-1 mb-2">
                                  {selections.map((item, itemIdx) => (
                                    <div key={itemIdx} className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-md px-3 py-2">
                                      <Check className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                                      <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-slate-800 truncate">{item.name}</p>
                                        {item.config_desc && <p className="text-[10px] text-slate-500 truncate">{item.config_desc}</p>}
                                        {item.mod_total > 0 && <p className="text-[10px] text-amber-600">+{sym}{item.mod_total.toFixed(2)} modifiers</p>}
                                      </div>
                                      <button onClick={() => removeComboSlotItem(slotIdx, itemIdx)} className="h-6 w-6 rounded bg-red-50 text-red-500 flex items-center justify-center hover:bg-red-100" data-testid={`combo-remove-item-${slotIdx}-${itemIdx}`}>
                                        <Trash2 className="h-3 w-3" />
                                      </button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Eligible products to pick from */}
                              {selections.length < needed && (
                                <div className="grid grid-cols-2 gap-1.5">
                                  {(slot.eligible_products || []).map((ep) => (
                                    <button
                                      key={ep.product_id}
                                      onClick={() => selectComboSlotItem(slotIdx, ep)}
                                      className="p-3 rounded-lg border border-slate-200 hover:border-[var(--brand-primary)] hover:bg-blue-50/30 text-left transition-all"
                                      data-testid={`combo-pick-${slotIdx}-${ep.product_id}`}
                                    >
                                      <p className="text-sm font-medium text-slate-800 truncate">{ep.name}</p>
                                      <p className="text-xs text-slate-400">{sym}{(ep.price || 0).toFixed(2)}</p>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </ScrollArea>

              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                {comboStep === "slots" && comboProduct.resolved_size_group && (
                  <Button variant="outline" onClick={() => { setComboStep("size"); setComboSlotSelections({}); setComboCurrentSlotIdx(0); }} data-testid="combo-back-to-size">
                    Back
                  </Button>
                )}
                <div className="flex-1" />
                {comboStep === "size" && (
                  <Button
                    onClick={handleComboSizeNext}
                    disabled={!comboSelectedSize}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
                    data-testid="combo-size-next"
                  >
                    Next <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                {comboStep === "slots" && (
                  <Button
                    onClick={addComboToCart}
                    disabled={!isComboComplete()}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
                    data-testid="combo-add-to-cart"
                  >
                    Add Combo · {sym}{getComboTotalPrice().toFixed(2)}
                  </Button>
                )}
              </div>
            </>
          ) : comboProduct && comboItemConfig ? (
            /* Sub-config: Configure modifiers for a picked combo item */
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-lg">
                  Configure: {comboItemConfig.product.name}
                </DialogTitle>
                <DialogDescription asChild>
                  <div className="text-sm text-slate-500 flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">
                      Part of {comboProduct.name}
                    </Badge>
                    {comboItemSelectedSize && <Badge variant="outline" className="text-[10px]">{comboItemSelectedSize.name}</Badge>}
                    {comboItemConfigStep === "modifiers" && comboItemConfig.configData.resolved_modifier_groups?.length > 1 && (
                      <span className="text-xs text-slate-400 ml-auto">Step {comboItemModGroupIdx + 1} of {comboItemConfig.configData.resolved_modifier_groups.length}</span>
                    )}
                  </div>
                </DialogDescription>
              </DialogHeader>

              <ScrollArea className="flex-1 -mx-6 px-6">
                {/* Size selection for combo sub-item */}
                {comboItemConfigStep === "size" && comboItemConfig.configData.resolved_size_group && (
                  <div className="space-y-3" data-testid="combo-item-size-step">
                    <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Select Size</h3>
                    <div className="grid grid-cols-2 gap-2">
                      {(comboItemConfig.configData.resolved_size_group.sizes || []).map((size) => {
                        const isSelected = comboItemSelectedSize?.size_id === size.size_id;
                        return (
                          <button
                            key={size.size_id}
                            onClick={() => { setComboItemSelectedSize(size); setComboItemModifiers({}); setComboItemModGroupIdx(0); }}
                            className={`p-4 rounded-lg border-2 text-left transition-all ${isSelected ? "border-[var(--brand-primary)] bg-blue-50 shadow-sm" : "border-slate-200 hover:border-slate-300"}`}
                            data-testid={`combo-item-size-${size.size_id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-semibold text-slate-900">{size.name}</p>
                                <p className="text-xs text-slate-400">{size.short_name}</p>
                              </div>
                              {isSelected && <Check className="h-5 w-5 text-[var(--brand-primary)]" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Modifier selection for combo sub-item */}
                {comboItemConfigStep === "modifiers" && comboItemConfig.configData.resolved_modifier_groups?.length > 0 && (() => {
                  const group = comboItemConfig.configData.resolved_modifier_groups[comboItemModGroupIdx];
                  if (!group) return null;
                  const gid = group.modifier_group_id;
                  const selected = comboItemModifiers[gid] || [];
                  const min = group.min_select || 0;
                  const max = group.max_select || 99;
                  const freeAmt = group.free_amount || 0;
                  const sizeAvail = comboItemConfig.configData.modifier_size_availability || {};
                  const availMods = (group.modifiers || []).filter((mod) => {
                    const allowed = sizeAvail[mod.modifier_id];
                    if (!allowed) return true;
                    if (!comboItemSelectedSize) return true;
                    return allowed.includes(comboItemSelectedSize.size_id);
                  });
                  return (
                    <div className="space-y-3" data-testid="combo-item-mod-step">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="text-sm font-bold text-slate-900">{group.name}</h3>
                          <p className="text-xs text-slate-400">
                            {min > 0 ? `Select ${min}` : "Optional"}{max < 99 ? ` (max ${max})` : ""}
                            {freeAmt > 0 ? ` · First ${freeAmt} free` : ""}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-xs">{selected.length}{max < 99 ? `/${max}` : ""} selected</Badge>
                      </div>
                      <div className="space-y-1.5">
                        {availMods.map((mod) => {
                          const isSelected = selected.some((s) => s.modifier_id === mod.modifier_id);
                          const selectedMod = selected.find((s) => s.modifier_id === mod.modifier_id);
                          const modPrice = getComboItemModPrice(mod, comboItemSelectedSize, comboItemConfig.configData);
                          const selIdx = isSelected ? selected.indexOf(selectedMod) : -1;
                          const isFree = isSelected && selIdx < freeAmt;
                          const willBeFree = !isSelected && selected.length < freeAmt && freeAmt > 0;
                          const hasSplit = mod.allow_split === true;
                          return (
                            <div key={mod.modifier_id} className={`rounded-lg border-2 transition-all ${isSelected ? "border-[var(--brand-primary)] bg-blue-50" : "border-slate-200 hover:border-slate-300"}`} data-testid={`combo-item-mod-${mod.modifier_id}`}>
                              <button
                                onClick={() => {
                                  const current = comboItemModifiers[gid] || [];
                                  const existIdx = current.findIndex((m) => m.modifier_id === mod.modifier_id);
                                  if (existIdx >= 0) {
                                    setComboItemModifiers((prev) => ({ ...prev, [gid]: current.filter((_, i) => i !== existIdx) }));
                                  } else if (current.length < max) {
                                    setComboItemModifiers((prev) => ({ ...prev, [gid]: [...current, { ...mod, placement: "whole", qty: 1 }] }));
                                  }
                                }}
                                className="w-full p-3 flex items-center justify-between text-left"
                                disabled={!isSelected && selected.length >= max}
                              >
                                <div className="flex items-center gap-2">
                                  <div className={`h-5 w-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${isSelected ? "bg-[var(--brand-primary)] border-[var(--brand-primary)]" : "border-slate-300"}`}>
                                    {isSelected && <Check className="h-3 w-3 text-white" />}
                                  </div>
                                  {mod.image_url && <img src={mod.image_url} alt="" className="h-8 w-8 rounded object-cover border border-slate-200 flex-shrink-0" loading="lazy" />}
                                  <span className="text-sm font-medium text-slate-900">{mod.name}</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  {modPrice > 0 && <span className={`text-sm font-semibold ${isFree || willBeFree ? "text-slate-400 line-through" : "text-slate-700"}`}>+{sym}{modPrice.toFixed(2)}</span>}
                                  {(isFree || willBeFree) && <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200">FREE</Badge>}
                                </div>
                              </button>
                              {isSelected && hasSplit && (
                                <div className="px-3 pb-3">
                                  <div className="flex gap-1.5" data-testid={`combo-mod-placement-${mod.modifier_id}`}>
                                    {["whole", "side_a", "side_b"].map((p) => (
                                      <button
                                        key={p}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          setComboItemModifiers((prev) => ({
                                            ...prev,
                                            [gid]: (prev[gid] || []).map((m) => m.modifier_id === mod.modifier_id ? { ...m, placement: p } : m)
                                          }));
                                        }}
                                        className={`flex-1 px-2 py-1.5 rounded-md text-xs font-medium transition-colors ${(selectedMod.placement || "whole") === p ? "bg-[var(--brand-primary)] text-white shadow-sm" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                                      >
                                        {p === "whole" ? "Whole" : p === "side_a" ? "Side A" : "Side B"}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                              )}
                              {isSelected && (
                                <div className="px-3 pb-3 flex items-center justify-between" data-testid={`combo-mod-qty-${mod.modifier_id}`}>
                                  <span className="text-xs text-slate-500">Quantity</span>
                                  <div className="flex items-center gap-1.5">
                                    <button onClick={(e) => { e.stopPropagation(); setComboItemModifiers((prev) => ({ ...prev, [gid]: (prev[gid] || []).map((m) => m.modifier_id === mod.modifier_id ? { ...m, qty: Math.max(1, (m.qty || 1) - 1) } : m) })); }} className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200"><Minus className="h-3 w-3" /></button>
                                    <span className="w-6 text-center text-sm font-bold">{selectedMod?.qty || 1}</span>
                                    <button onClick={(e) => { e.stopPropagation(); setComboItemModifiers((prev) => ({ ...prev, [gid]: (prev[gid] || []).map((m) => m.modifier_id === mod.modifier_id ? { ...m, qty: (m.qty || 1) + 1 } : m) })); }} className="h-7 w-7 rounded bg-slate-100 flex items-center justify-center hover:bg-slate-200"><Plus className="h-3 w-3" /></button>
                                  </div>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </ScrollArea>

              <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
                <Button variant="outline" onClick={() => {
                  if (comboItemConfigStep === "modifiers" && comboItemModGroupIdx > 0) {
                    setComboItemModGroupIdx(comboItemModGroupIdx - 1);
                  } else if (comboItemConfigStep === "modifiers" && comboItemConfig.configData.resolved_size_group) {
                    setComboItemConfigStep("size");
                  } else {
                    setComboItemConfig(null);
                  }
                }} data-testid="combo-item-back">Back</Button>
                <div className="flex-1" />
                {comboItemConfigStep === "size" && (
                  <Button
                    onClick={() => {
                      if (!comboItemSelectedSize) { toast.error("Please select a size"); return; }
                      if (comboItemConfig.configData.resolved_modifier_groups?.length > 0) {
                        setComboItemConfigStep("modifiers");
                      } else {
                        confirmComboItemConfig();
                      }
                    }}
                    disabled={!comboItemSelectedSize}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
                    data-testid="combo-item-size-next"
                  >
                    {comboItemConfig.configData.resolved_modifier_groups?.length > 0 ? "Next" : "Confirm"} <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
                {comboItemConfigStep === "modifiers" && (
                  <Button
                    onClick={() => {
                      const groups = comboItemConfig.configData.resolved_modifier_groups || [];
                      const currentGroup = groups[comboItemModGroupIdx];
                      const minSel = currentGroup?.min_select || 0;
                      const selected = (comboItemModifiers[currentGroup?.modifier_group_id] || []).length;
                      if (selected < minSel) { toast.error(`Select at least ${minSel} for ${currentGroup.name}`); return; }
                      if (comboItemModGroupIdx < groups.length - 1) {
                        setComboItemModGroupIdx(comboItemModGroupIdx + 1);
                      } else {
                        confirmComboItemConfig();
                      }
                    }}
                    className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white"
                    data-testid="combo-item-mod-next"
                  >
                    {comboItemModGroupIdx < (comboItemConfig.configData.resolved_modifier_groups?.length || 1) - 1 ? (
                      <>Next <ChevronRight className="h-4 w-4 ml-1" /></>
                    ) : (
                      "Confirm Item"
                    )}
                  </Button>
                )}
              </div>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Discount Code Entry Dialog */}
      <Dialog open={showCodeEntry} onOpenChange={setShowCodeEntry}>
        <DialogContent className="sm:max-w-sm" data-testid="code-entry-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2"><Tag className="h-5 w-5 text-[var(--brand-primary)]" /> Enter Discount Code</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <Input
              value={discountCode}
              onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
              placeholder="Enter code..."
              className="font-mono uppercase text-center text-lg h-12"
              data-testid="discount-code-input"
              autoFocus
              onKeyDown={(e) => { if (e.key === "Enter") redeemCode(); }}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCodeEntry(false)}>Cancel</Button>
            <Button onClick={redeemCode} className="bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white" data-testid="redeem-code-btn">Apply Code</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
